import moment from 'moment';
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';

import { PageContext } from '../page';
import {
  TripsMonitoredReportPath,
  MessageConfigurationPath,
  TestMessagingPath,
  ManageUserPath,
  FlightSearchPath,
  ManageTripGroupsPath,
  TripAlertBrandingPath,
} from '../../../appRoutes';
import { getProperty } from '../../../fsConfig';
import { SUPER_ADMIN_ACCOUNT_ID } from '../../../constants';

const weekAgo = (): string => {
  const dateFormatString = 'YYYY-MM-DD';
  return moment()
    .subtract(7, 'd')
    .format(dateFormatString);
};

const openFlyout = (pushAlert: (type: string, message: string) => void) => {
  // TODO: this smells... likely a chance to improve the user experience. Seems like there's a reason modern web stuff doesn't use popover windows anymore.
  const flyout = window.open(
    '/issues/flyout',
    'IssuesFlyout',
    'scrollbars=1,resizable=0,width=385,height=650',
  );
  if (!flyout) {
    pushAlert(
      'danger',
      'ISSUES FLYOUT: Pop-ups appear to be blocked by the browser. Review your browser settings to allow for the issues flyout.',
    );
  }
};

const Navigation: React.FC = (): JSX.Element => {
  const { pushAlert } = useContext(PageContext);
  const user = getProperty('session', {});
  
  // Console log to see all features and permissions from backend
  const {
    canIssueReports,
    canManageIssues,
    canViewTravelerMap,
    canViewWaivers,
    canUseWaiverReports,
    isAccountAdmin,
    isWaiverAdmin,
    multiTabView,
    showAdmin,
  } = user;

  // Check if user is Super Admin in sudo session (has suUid from original Super Admin session)
  const isSuperAdminInSudo = user?.suUid && user?.suUsername;
  
  // Enhanced Traveler Map access: regular feature flag OR Super Admin in sudo session
  const canAccessTravelerMap = canViewTravelerMap() || isSuperAdminInSudo;

  return (
    <>
      {multiTabView() && (
        <div className="row navigation">
          <nav
            className="row multi-nav"
          >
            <div className="col-md-12">
              <div id="nav-menu">
                <ul role="menu">
                  {canAccessTravelerMap && (
                    <li>
                      <a href="/map" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        TRAVELER MAP
                        <span style={{
                          marginLeft: '8px',
                          padding: '4px 12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          background: '#4580D9',
                          color: '#ffffff',
                          borderRadius: '50px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          border: '2px solid #8BB6F7'
                        }}>
                          BETA
                        </span>
                      </a>
                    </li>
                  )}
                  {(canIssueReports() || canManageIssues()) && (
                    <li>
                      TRIPS<i className="icon-arrow"></i>
                      <ul>
                        <a href={`/trips/search?weekAgo=${weekAgo()}`}>
                          <li>TRIP SEARCH</li>
                        </a>
                        <a href={FlightSearchPath}>
                          <li>FLIGHT SEARCH</li>
                        </a>
                        {(isAccountAdmin() || showAdmin()) && (
                          <Link to={TripsMonitoredReportPath}>
                            <li>TRIPS MONITORED REPORT</li>
                          </Link>
                        )}
                      </ul>
                    </li>
                  )}
                  {(canIssueReports() || canManageIssues()) && (
                    <li>
                      ISSUES<i className="icon-arrow"></i>
                      <ul>
                        {canManageIssues() && (
                          <a href="/issues/open">
                            <li>OPEN ISSUE LIST</li>
                          </a>
                        )}
                        {canManageIssues() && (
                          <a href="/issues/my">
                            <li>MY ISSUE LIST</li>
                          </a>
                        )}
                        {canManageIssues() && (
                          <a href="/issues/search">
                            <li>ISSUE SEARCH</li>
                          </a>
                        )}
                        {canIssueReports() && (
                          <a href="/issues/reporting">
                            <li>ISSUE REPORTS</li>
                          </a>
                        )}
                      </ul>
                    </li>
                  )}
                  {(canViewWaivers() ||
                    isWaiverAdmin() ||
                    canUseWaiverReports()) && (
                    <li>
                      WAIVERS<i className="icon-arrow"></i>
                      <ul>
                        {canViewWaivers() && (
                          <a href="/waivers">
                            <li>WAIVER LIST</li>
                          </a>
                        )}
                        {isWaiverAdmin() && (
                          <a href="/waivers/workItems/list">
                            <li>WAIVER WORK ITEMS</li>
                          </a>
                        )}
                        {canUseWaiverReports() && (
                          <a href="/waivers/reporting">
                            <li>WAIVER REPORTING</li>
                          </a>
                        )}
                      </ul>
                    </li>
                  )}

                  <a href="/agent/traveler/profile/search">
                    <li>TRAVELER SEARCH</li>
                  </a>

                  {isAccountAdmin() && (
                    <li>
                      ACCOUNT SETTINGS<i className="icon-arrow"></i>
                      <ul>
                        {isAccountAdmin() && (
                          <Link to={TestMessagingPath}>
                            <li>TEST MESSAGING</li>
                          </Link>
                        )}
                        {isAccountAdmin() && (
                          // this one is not pulled from a variable because it's not internal to the react SPA, it's a link out to the angular SPA
                          <Link to={TripAlertBrandingPath}>
                            <li>TRIP ALERT BRANDING</li>
                          </Link>
                        )}
                        {isAccountAdmin() && (
                          <Link to={MessageConfigurationPath}>
                            <li>EMAIL CONFIGURATION</li>
                          </Link>
                        )}
                        {isAccountAdmin() && (
                          <Link to={ManageUserPath}>
                            <li>MANAGE USERS</li>
                          </Link>
                        )}
                        {isAccountAdmin() && (
                          <Link to={ManageTripGroupsPath}>
                            <li>MANAGE TRIP GROUPS</li>
                          </Link>
                        )}
                      </ul>
                    </li>
                  )}
                  {(user?.accountId === SUPER_ADMIN_ACCOUNT_ID && showAdmin()) && (
                    <a href="/admin">
                      <li>ADMIN</li>
                    </a>
                  )}
                </ul>
              </div>
              {canManageIssues() && (
                <div className="hidden-xs just-a-link">
                  <div
                    className="flyout"
                    onClick={() => openFlyout(pushAlert)}
                    title="Issues Flyout"
                  >
                    <i className="sprites sprite-flyout-normal"></i>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navigation;
